import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTopProviders } from '../hooks/useTopProviders';
import { useProviderStats } from '../hooks/useProviderStats';
import { useMyProvider } from '../hooks/useMyProvider';
import { useNearbyProviders } from '../hooks/useNearbyProviders';
import SearchBar from '../components/SearchBar';
import EndorseModal from '../components/EndorseModal';
import NotAvailable from '../components/Common/NotAvailable';

// Icons as SVG components

const NotificationIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UserIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const VerifiedIcon = () => (
  <div className="bg-[#c6a664] rounded-full p-1 inline-flex">
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14 4L6 12L2 8"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

const StarIcon = ({ filled = false }: { filled?: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill={filled ? '#c6a664' : 'none'}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 1L10.163 5.279L15 6.00L11.5 9.279L12.326 14L8 11.779L3.674 14L4.5 9.279L1 6.00L5.837 5.279L8 1Z"
      stroke="#c6a664"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EndorsementIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
      fill="#c6a664"
      stroke="#c6a664"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckboxIcon = ({ checked = false }: { checked?: boolean }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect
      x="1"
      y="1"
      width="18"
      height="18"
      rx="3"
      fill={checked ? '#c6a664' : 'white'}
      stroke={checked ? '#c6a664' : '#e5e5ea'}
      strokeWidth="2"
    />
    {checked && (
      <path
        d="M5 10L8.5 13.5L15 7"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </svg>
);

// "Glow Med Spa" -> "GMS"
const getNameInitials = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notice, setNotice] = useState<string | null>(
    (location.state as { notice?: string } | null)?.notice || null
  );
  const { providers: topProviders, loading: topProvidersLoading } = useTopProviders(5);
  const { stats: providerStats, loading: statsLoading } = useProviderStats();
  const { provider: myProvider, loading: myProviderLoading } = useMyProvider();
  const { providers: nearbyProviders, loading: nearbyProvidersLoading } = useNearbyProviders(3);
  const [endorseModal, setEndorseModal] = useState<{
    isOpen: boolean;
    providerName: string;
    providerId: number | null;
  }>({
    isOpen: false,
    providerName: '',
    providerId: null,
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/signin');
  };

  const handleEndorse = (providerId: number, providerName: string) => {
    setEndorseModal({ isOpen: true, providerName, providerId });
  };

  const handleProviderClick = (providerId: number) => {
    navigate(`/provider/${providerId}`);
  };

  // Real provider/user data only. Missing provider fields are null and render as
  // <NotAvailable />; non-providers have no score, specialty or location to show.
  const displayName = myProvider?.business_name || user?.username || '';
  const providerProfile = {
    name: displayName,
    score: myProvider && typeof myProvider.clout_score === 'number' ? myProvider.clout_score : null,
    specialty: myProvider ? myProvider.specialties?.[0] || null : null,
    location: myProvider
      ? [myProvider.city, myProvider.state].filter(Boolean).join(', ') || null
      : null,
    certificationProgress: myProvider?.profile_completion_percentage || 0,
    // TODO: Add profile_image_url to User type so non-providers get a picture too
    profilePicture: myProvider?.user_profile_image_url || null,
    isVerified: !!myProvider && myProvider.is_certified > 0,
    initials: myProvider?.business_name
      ? getNameInitials(myProvider.business_name)
      : (user?.username || '').substring(0, 2).toUpperCase(),
  };

  // The #1 provider by CLOUT score, featured in the spotlight
  const spotlightProvider = topProviders.length > 0 ? topProviders[0] : null;

  // Provider stats from the API; the zero defaults only feed the profile checklist.
  // The overview cards show <NotAvailable /> when the stats failed to load.
  const stats = providerStats || {
    endorsements_received: 0,
    endorsements_given: 0,
    endorsed_by_top_scorers: 0,
  };

  return (
    <div className="min-h-screen bg-[#f8f6f2]">
      {/* Header */}
      <header className="bg-[#f8f6f2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 hover:bg-white/50 rounded-full transition-colors"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Logo */}
            <div className="flex items-center">
              <h1 className="text-xl md:text-2xl font-['Ovo'] text-[#1f1f1f]">CLOUT</h1>
              <span className="ml-2 text-xs font-['Open_Sans'] text-[#767472] uppercase tracking-wider hidden sm:inline">
                AESTHETICS
              </span>
            </div>

            {/* Search Bar - Hidden on mobile */}
            <div className="hidden lg:block flex-1 max-w-lg mx-8">
              <SearchBar placeholder="Search for providers" isAuthenticated={true} />
            </div>

            {/* Right Icons */}
            <div className="flex items-center space-x-2 md:space-x-4">
              <button className="p-2 hover:bg-white/50 rounded-full transition-colors">
                <NotificationIcon />
              </button>
              <button
                onClick={() => navigate('/profile')}
                className="p-2 hover:bg-white/50 rounded-full transition-colors"
              >
                <UserIcon />
              </button>
              {/* Mobile Right Sidebar Toggle */}
              <button
                onClick={() => setRightSidebarOpen(!rightSidebarOpen)}
                className="lg:hidden p-2 hover:bg-white/50 rounded-full transition-colors"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                    stroke="#1f1f1f"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-t border-b border-[#eaeaea]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center overflow-x-auto no-scrollbar">
            <button className="px-4 sm:px-8 py-3 text-xs sm:text-sm font-['Ovo'] text-white uppercase bg-[#1f1f1f] whitespace-nowrap min-h-[44px]">
              HOME
            </button>
            <button
              onClick={() => navigate('/endorsements')}
              className="px-4 sm:px-8 py-3 text-xs sm:text-sm font-['Ovo'] text-[#1f1f1f] uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors whitespace-nowrap min-h-[44px]"
            >
              ENDORSEMENTS
            </button>
            <button
              onClick={() => navigate('/whats-hot')}
              className="px-4 sm:px-8 py-3 text-xs sm:text-sm font-['Ovo'] text-[#1f1f1f] uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors whitespace-nowrap min-h-[44px]"
            >
              WHAT'S HOT!
            </button>
            {myProvider && (
              <button
                onClick={() => navigate('/dashboard/analytics')}
                className="px-4 sm:px-8 py-3 text-xs sm:text-sm font-['Ovo'] text-[#1f1f1f] uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors whitespace-nowrap min-h-[44px]"
              >
                ANALYTICS
              </button>
            )}
          </div>
        </div>
      </nav>

      {notice && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div
            className="flex items-start justify-between gap-3 p-3 bg-orange-50 border border-orange-200 rounded font-['Open_Sans'] text-sm text-orange-800"
            role="status"
          >
            <span>{notice}</span>
            <button
              onClick={() => setNotice(null)}
              className="text-orange-800 hover:text-orange-900"
              aria-label="Dismiss"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Mobile Search Bar */}
      <div className="lg:hidden bg-white border-b border-[#eaeaea] px-4 py-3">
        <SearchBar placeholder="Search for providers" isAuthenticated={true} />
      </div>

      <div className="flex max-w-7xl mx-auto relative">
        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Left Sidebar - Full Height White Background */}
        <div
          className={`fixed lg:relative inset-y-0 left-0 transform ${
            mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 transition-transform duration-300 ease-in-out w-80 bg-white min-h-[calc(100vh-120px)] px-6 py-8 border-r border-[#eaeaea] z-50 lg:z-auto overflow-y-auto`}
        >
          {/* Provider Profile */}
          <div className="text-center mb-8" data-testid="profile-summary">
            {myProviderLoading ? (
              <div className="animate-pulse">
                <div className="w-32 h-32 bg-gray-200 rounded-full mx-auto mb-4"></div>
                <div className="h-6 bg-gray-200 rounded w-3/4 mx-auto mb-3"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto"></div>
              </div>
            ) : (
              <>
                <div className="relative inline-block mb-4">
                  <div className="w-32 h-32 bg-gray-300 rounded-full mx-auto flex items-center justify-center ring-4 ring-[#c6a664] overflow-hidden">
                    {providerProfile.profilePicture ? (
                      <img
                        src={providerProfile.profilePicture}
                        alt={providerProfile.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-3xl font-['Open_Sans'] font-semibold text-gray-600">
                        {providerProfile.initials}
                      </span>
                    )}
                  </div>
                  {providerProfile.isVerified && (
                    <div className="absolute bottom-2 right-2">
                      <VerifiedIcon />
                    </div>
                  )}
                </div>
                <h3 className="font-['Ovo'] text-xl text-[#1f1f1f] mb-3">{providerProfile.name}</h3>
                {/* Only providers have a CLOUT score */}
                {providerProfile.score !== null && (
                  <div className="flex items-center justify-center space-x-1 mb-2">
                    <StarIcon filled />
                    <span className="font-['Open_Sans'] text-base text-[#1f1f1f]">
                      {providerProfile.score.toFixed(1)} Clout Score
                    </span>
                  </div>
                )}
                {!myProvider ? (
                  <p className="font-['Open_Sans'] text-sm text-[#767472]">Not a provider</p>
                ) : (
                  <div className="space-y-1">
                    <div className="flex items-center justify-center space-x-2 text-[#767472]">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14 6.66667C14 11.3333 8 15.3333 8 15.3333C8 15.3333 2 11.3333 2 6.66667C2 5.07537 2.63214 3.54924 3.75736 2.42402C4.88258 1.29881 6.4087 0.666672 8 0.666672C9.5913 0.666672 11.1174 1.29881 12.2426 2.42402C13.3679 3.54924 14 5.07537 14 6.66667Z"
                          stroke="#c6a664"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 8.66667C9.10457 8.66667 10 7.77124 10 6.66667C10 5.5621 9.10457 4.66667 8 4.66667C6.89543 4.66667 6 5.5621 6 6.66667C6 7.77124 6.89543 8.66667 8 8.66667Z"
                          stroke="#c6a664"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="font-['Open_Sans'] text-sm">
                        {providerProfile.specialty ?? <NotAvailable />}
                      </span>
                    </div>
                    <div className="flex items-center justify-center space-x-2 text-[#767472]">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z"
                          stroke="#c6a664"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 4V8L10.5 9.5"
                          stroke="#c6a664"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="font-['Open_Sans'] text-sm">
                        {providerProfile.location ?? <NotAvailable />}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Complete Profile Section - Only show for providers */}
          {myProvider && (
            <div className="mb-8">
              <h4 className="font-['Open_Sans'] text-base font-semibold text-[#1f1f1f] mb-3">
                Complete your Profile
              </h4>
              <p className="font-['Open_Sans'] text-sm text-[#767472] mb-4">
                {providerProfile.certificationProgress}% to CLOUT Certification
              </p>
              <div className="mb-6">
                <div className="w-full bg-[#f8f6f2] rounded-full h-2 mb-2">
                  <div
                    className="bg-[#c6a664] h-2 rounded-full transition-all duration-300"
                    style={{ width: `${providerProfile.certificationProgress}%` }}
                  />
                </div>
                <p className="font-['Open_Sans'] text-xs text-[#767472]">
                  {providerProfile.certificationProgress}% complete
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <CheckboxIcon checked={!!myProvider.business_name} />
                  <span
                    className={`font-['Open_Sans'] text-sm ${
                      myProvider.business_name ? 'text-[#1f1f1f] line-through' : 'text-[#767472]'
                    }`}
                  >
                    Add Basic Details
                  </span>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <CheckboxIcon checked={!!myProvider.user_profile_image_url} />
                  <span
                    className={`font-['Open_Sans'] text-sm ${
                      myProvider.user_profile_image_url
                        ? 'text-[#1f1f1f] line-through'
                        : 'text-[#767472]'
                    }`}
                  >
                    Add Profile Picture
                  </span>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <CheckboxIcon checked={myProvider.education && myProvider.education.length > 0} />
                  <span
                    className={`font-['Open_Sans'] text-sm ${
                      myProvider.education && myProvider.education.length > 0
                        ? 'text-[#1f1f1f] line-through'
                        : 'text-[#767472]'
                    }`}
                  >
                    Add Education
                  </span>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <CheckboxIcon checked={(myProvider.years_of_experience ?? 0) > 0} />
                  <span
                    className={`font-['Open_Sans'] text-sm ${
                      (myProvider.years_of_experience ?? 0) > 0
                        ? 'text-[#1f1f1f] line-through'
                        : 'text-[#767472]'
                    }`}
                  >
                    Add Experience
                  </span>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <CheckboxIcon checked={stats.endorsements_given > 0} />
                  <span
                    className={`font-['Open_Sans'] text-sm ${
                      stats.endorsements_given > 0
                        ? 'text-[#1f1f1f] line-through'
                        : 'text-[#767472]'
                    }`}
                  >
                    Endorse a Provider
                  </span>
                </label>
              </div>

              <button
                onClick={() => navigate('/profile/complete')}
                className="w-full px-4 py-2.5 bg-transparent border border-[#1f1f1f] text-[#1f1f1f] font-['Open_Sans'] text-sm font-semibold uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors"
              >
                COMPLETE MY PROFILE
              </button>
            </div>
          )}

          {/* Become a Provider Section - Only show for non-providers */}
          {!myProvider && !myProviderLoading && (
            <div className="mb-8 p-4 bg-[#f8f6f2] rounded-lg">
              <h4 className="font-['Open_Sans'] text-base font-semibold text-[#1f1f1f] mb-2">
                Become a Provider
              </h4>
              <p className="font-['Open_Sans'] text-sm text-[#767472] mb-4">
                Join our network of aesthetic professionals and start building your CLOUT score.
              </p>
              <button
                onClick={() => navigate('/profile')}
                className="w-full px-4 py-2.5 bg-[#c6a664] text-white font-['Open_Sans'] text-sm font-semibold uppercase hover:bg-[#b89854] transition-colors"
              >
                BECOME A PROVIDER
              </button>
            </div>
          )}

          {/* Settings and Logout */}
          <div className="space-y-2">
            <button
              onClick={() => navigate('/profile')}
              className="flex items-center space-x-3 w-full px-4 py-2.5 text-left hover:bg-[#f8f6f2] rounded-lg transition-colors"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z"
                  stroke="#c6a664"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16.167 12.5C16.0674 12.7513 16.0416 13.0253 16.0926 13.2908C16.1437 13.5564 16.2697 13.8029 16.4563 14.0013L16.5013 14.0463C16.6503 14.1952 16.7685 14.3722 16.8491 14.5668C16.9297 14.7613 16.9712 14.9697 16.9712 15.1801C16.9712 15.3904 16.9297 15.5988 16.8491 15.7934C16.7685 15.9879 16.6503 16.1649 16.5013 16.3138C16.3524 16.4628 16.1754 16.581 15.9808 16.6616C15.7863 16.7422 15.5779 16.7837 15.3675 16.7837C15.1572 16.7837 14.9488 16.7422 14.7542 16.6616C14.5597 16.581 14.3827 16.4628 14.2338 16.3138L14.1888 16.2688C13.9903 16.0822 13.7439 15.9562 13.4783 15.9051C13.2128 15.8541 12.9388 15.8799 12.6875 15.9795C12.4413 16.0732 12.2244 16.2343 12.0607 16.4448C11.897 16.6554 11.7926 16.9073 11.7588 17.1738V17.2917C11.7588 17.7337 11.5831 18.1576 11.2705 18.4701C10.958 18.7827 10.5341 18.9583 10.0921 18.9583C9.65003 18.9583 9.22614 18.7827 8.91358 18.4701C8.60103 18.1576 8.42544 17.7337 8.42544 17.2917V17.2295C8.40646 16.9495 8.30819 16.6793 8.14071 16.4492C7.97323 16.219 7.74255 16.0377 7.47252 15.9259C7.22123 15.8263 6.94722 15.8005 6.68167 15.8515C6.41611 15.9026 6.16965 16.0286 5.97127 16.2151L5.92627 16.2601C5.77734 16.4091 5.60034 16.5273 5.40579 16.6079C5.21125 16.6885 5.00282 16.73 4.79252 16.73C4.58221 16.73 4.37378 16.6885 4.17924 16.6079C3.98469 16.5273 3.80769 16.4091 3.65877 16.2601C3.50975 16.1112 3.39158 15.9342 3.31098 15.7397C3.23038 15.5451 3.18885 15.3367 3.18885 15.1264C3.18885 14.9161 3.23038 14.7077 3.31098 14.5131C3.39158 14.3186 3.50975 14.1416 3.65877 13.9926L3.70377 13.9476C3.89035 13.7493 4.01631 13.5028 4.06738 13.2373C4.11844 12.9717 4.09262 12.6977 3.99294 12.4465C3.89924 12.2002 3.73811 11.9833 3.52757 11.8197C3.31703 11.656 3.06513 11.5516 2.79877 11.5177L2.68085 11.5177C2.23886 11.5177 1.81497 11.3421 1.50241 11.0296C1.18986 10.717 1.01427 10.2931 1.01427 9.85108C1.01427 9.40909 1.18986 8.9852 1.50241 8.67264C1.81497 8.36009 2.23886 8.1845 2.68085 8.1845H2.74294C3.02295 8.16552 3.29316 8.06725 3.52334 7.89977C3.75351 7.73229 3.93481 7.50161 4.04627 7.23158C4.14595 6.98029 4.17176 6.70629 4.1207 6.44073C4.06965 6.17517 3.94369 5.92871 3.7571 5.73033L3.71211 5.68533C3.56308 5.53641 3.44491 5.35941 3.36431 5.16486C3.28371 4.97032 3.24219 4.76189 3.24219 4.55158C3.24219 4.34128 3.28371 4.13285 3.36431 3.9383C3.44491 3.74376 3.56308 3.56676 3.71211 3.41783C3.86103 3.26881 4.03803 3.15064 4.23258 3.07004C4.42712 2.98944 4.63555 2.94791 4.84585 2.94791C5.05616 2.94791 5.26459 2.98944 5.45913 3.07004C5.65368 3.15064 5.83068 3.26881 5.9796 3.41783L6.0246 3.46283C6.22298 3.64942 6.46945 3.77538 6.735 3.82644C7.00056 3.87751 7.27456 3.85169 7.52585 3.75201C7.77209 3.65831 7.98896 3.49718 8.15253 3.28664C8.31609 3.0761 8.42036 2.82421 8.45419 2.55783V2.43992C8.45419 1.99793 8.62978 1.57404 8.94233 1.26148C9.25489 0.948926 9.67878 0.773335 10.1208 0.773335C10.5628 0.773335 10.9866 0.948926 11.2992 1.26148C11.6117 1.57404 11.7873 1.99793 11.7873 2.43992V2.502C11.8212 2.76837 11.9254 3.02026 12.089 3.2308C12.2526 3.44134 12.4695 3.60247 12.7158 3.69617C12.967 3.79585 13.241 3.82166 13.5066 3.77061C13.7721 3.71955 14.0186 3.59359 14.217 3.407L14.262 3.362C14.4109 3.21298 14.5879 3.09481 14.7825 3.01421C14.977 2.93361 15.1854 2.89208 15.3958 2.89208C15.6061 2.89208 15.8145 2.93361 16.009 3.01421C16.2036 3.09481 16.3806 3.21298 16.5295 3.362C16.6785 3.51092 16.7967 3.68792 16.8773 3.88247C16.9579 4.07701 16.9994 4.28544 16.9994 4.49575C16.9994 4.70605 16.9579 4.91448 16.8773 5.10903C16.7967 5.30357 16.6785 5.48057 16.5295 5.6295L16.4845 5.6745C16.2979 5.87287 16.172 6.11934 16.1209 6.3849C16.0698 6.65045 16.0957 6.92446 16.1953 7.17575C16.289 7.42199 16.4502 7.63886 16.6607 7.80242C16.8713 7.96599 17.1232 8.07025 17.3895 8.10408L17.5075 8.10408C17.9494 8.10408 18.3733 8.27967 18.6859 8.59223C18.9984 8.90478 19.174 9.32867 19.174 9.77066C19.174 10.2127 18.9984 10.6365 18.6859 10.9491C18.3733 11.2616 17.9494 11.4372 17.5075 11.4372H17.4453C17.179 11.4711 16.9271 11.5753 16.7165 11.739C16.506 11.9026 16.3448 12.1195 16.2512 12.3657L16.167 12.5Z"
                  stroke="#c6a664"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="font-['Open_Sans'] text-sm text-[#1f1f1f]">Settings</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-3 w-full px-4 py-2.5 text-left hover:bg-[#f8f6f2] rounded-lg transition-colors"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.5 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H7.5"
                  stroke="#c6a664"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.3333 14.1667L17.5 10L13.3333 5.83334"
                  stroke="#c6a664"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17.5 10H7.5"
                  stroke="#c6a664"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="font-['Open_Sans'] text-sm text-[#1f1f1f]">Log out</span>
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 bg-[#f8f6f2] min-h-[calc(100vh-120px)]">
          <div className="flex flex-col lg:grid lg:grid-cols-12 h-full">
            {/* Main Content */}
            <div className="lg:col-span-8 px-4 md:px-8 py-8">
              {/* Overview Section */}
              <div className="mb-8" data-testid="overview-stats">
                <h2 className="font-['Ovo'] text-lg md:text-xl text-[#1f1f1f] mb-4 md:mb-6">
                  OVERVIEW
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                    <div className="flex justify-center mb-2">
                      <EndorsementIcon />
                    </div>
                    <div className="font-['Open_Sans'] text-2xl font-semibold text-[#1f1f1f] mb-1">
                      {statsLoading ? (
                        <div className="animate-pulse h-8 w-12 bg-gray-200 rounded mx-auto"></div>
                      ) : providerStats ? (
                        stats.endorsements_received
                      ) : (
                        <NotAvailable className="text-sm font-normal" />
                      )}
                    </div>
                    <div className="font-['Open_Sans'] text-sm text-[#767472]">
                      Endorsements received
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                    <div className="flex justify-center mb-2">
                      <div className="w-6 h-6 bg-[#c6a664] rounded-full" />
                    </div>
                    <div className="font-['Open_Sans'] text-2xl font-semibold text-[#1f1f1f] mb-1">
                      {statsLoading ? (
                        <div className="animate-pulse h-8 w-12 bg-gray-200 rounded mx-auto"></div>
                      ) : providerStats ? (
                        stats.endorsements_given
                      ) : (
                        <NotAvailable className="text-sm font-normal" />
                      )}
                    </div>
                    <div className="font-['Open_Sans'] text-sm text-[#767472]">
                      Endorsements given
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                    <div className="flex justify-center mb-2">
                      <div className="w-6 h-6 bg-[#c6a664] rounded-full" />
                    </div>
                    <div className="font-['Open_Sans'] text-2xl font-semibold text-[#1f1f1f] mb-1">
                      {statsLoading ? (
                        <div className="animate-pulse h-8 w-12 bg-gray-200 rounded mx-auto"></div>
                      ) : providerStats ? (
                        stats.endorsed_by_top_scorers
                      ) : (
                        <NotAvailable className="text-sm font-normal" />
                      )}
                    </div>
                    <div className="font-['Open_Sans'] text-sm text-[#767472]">
                      Endorsed by top scorers
                    </div>
                  </div>
                </div>
              </div>

              {/* Search Section */}
              <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-8">
                <h3 className="font-['Ovo'] text-base md:text-lg text-[#1f1f1f] mb-4">
                  SEARCH FOR A VERIFIED AESTHETIC PROVIDER
                </h3>
                <SearchBar
                  placeholder="Search for providers"
                  isAuthenticated={true}
                  className="mb-4"
                />
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-['Open_Sans'] bg-[#f8f6f2] text-[#1f1f1f]">
                    <VerifiedIcon /> Verified
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-['Open_Sans'] bg-[#f8f6f2] text-[#1f1f1f]">
                    📍 Location
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-['Open_Sans'] bg-[#f8f6f2] text-[#1f1f1f]">
                    ⭐ Specialty
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-['Open_Sans'] bg-[#f8f6f2] text-[#1f1f1f] hidden sm:inline-flex">
                    📋 Endorsement Count
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-['Open_Sans'] bg-[#f8f6f2] text-[#1f1f1f] hidden sm:inline-flex">
                    🏆 Certification Level
                  </span>
                </div>
              </div>

              {/* Providers from Your Area */}
              <div data-testid="nearby-providers">
                <h3 className="font-['Ovo'] text-base md:text-lg text-[#1f1f1f] mb-4">
                  PROVIDERS FROM YOUR AREA
                </h3>
                {nearbyProvidersLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="bg-white rounded-lg shadow-sm p-4 animate-pulse">
                        <div className="w-20 h-20 bg-gray-200 rounded-full mx-auto mb-3"></div>
                        <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2 mx-auto mb-3"></div>
                        <div className="h-8 bg-gray-200 rounded"></div>
                      </div>
                    ))}
                  </div>
                ) : nearbyProviders.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {nearbyProviders.map((provider) => (
                      <div
                        key={provider.id}
                        className="bg-white rounded-lg shadow-sm p-4 text-center cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => handleProviderClick(provider.id)}
                      >
                        <div className="w-20 h-20 bg-gray-300 rounded-full mx-auto mb-3 flex items-center justify-center overflow-hidden">
                          {provider.profile_image_url ? (
                            <img
                              src={provider.profile_image_url}
                              alt={provider.business_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-xl font-['Open_Sans'] font-semibold text-gray-600">
                              {getNameInitials(provider.business_name)}
                            </span>
                          )}
                        </div>
                        <h4 className="font-['Open_Sans'] text-sm font-semibold text-[#1f1f1f] mb-1">
                          {provider.business_name}
                        </h4>
                        <p className="font-['Open_Sans'] text-xs text-[#767472] mb-2">
                          {provider.specialties?.[0] || <NotAvailable />}
                        </p>
                        <div className="flex items-center justify-center space-x-1 mb-3">
                          <StarIcon filled />
                          <span className="font-['Open_Sans'] text-xs font-semibold text-[#1f1f1f]">
                            {provider.clout_score.toFixed(1)} CLOUT SCORE
                          </span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEndorse(provider.id, provider.business_name);
                          }}
                          className="w-full px-3 py-1.5 bg-transparent border border-[#1f1f1f] text-[#1f1f1f] font-['Open_Sans'] text-xs font-semibold uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors"
                        >
                          ENDORSE
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Empty when the user has no provider profile, no known location,
                  // or nobody else in their city/state
                  <div className="text-center py-8">
                    <NotAvailable className="text-sm" />
                  </div>
                )}
              </div>
            </div>

            {/* Right Sidebar - Full Height White Background */}
            {/* Mobile Right Sidebar Overlay */}
            {rightSidebarOpen && (
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                onClick={() => setRightSidebarOpen(false)}
              />
            )}
            <div
              className={`fixed lg:relative inset-y-0 right-0 transform ${
                rightSidebarOpen ? 'translate-x-0' : 'translate-x-full'
              } lg:translate-x-0 transition-transform duration-300 ease-in-out lg:col-span-4 bg-white min-h-[calc(100vh-120px)] px-6 py-8 border-l border-[#eaeaea] w-80 lg:w-auto z-50 lg:z-auto overflow-y-auto`}
            >
              {/* Top Clout */}
              <div className="mb-12">
                <h3 className="font-['Ovo'] text-xl text-[#1f1f1f] mb-4 uppercase">TOP CLOUT</h3>
                <div className="border-t border-[#eaeaea]">
                  {topProvidersLoading ? (
                    <div className="py-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#c6a664] mx-auto"></div>
                    </div>
                  ) : (
                    topProviders.map((provider, index) => {
                      const isCurrentUser = user?.username === provider.username;
                      return (
                        <div
                          key={provider.id}
                          className={`border-b border-[#eaeaea] cursor-pointer hover:bg-gray-50 transition-colors ${
                            isCurrentUser
                              ? 'bg-[#c6a664] text-white hover:bg-[#b89854]'
                              : 'bg-white'
                          }`}
                          onClick={() => handleProviderClick(provider.id)}
                        >
                          <div className="flex items-center py-3 px-3 relative">
                            <span
                              className={`font-['Open_Sans'] text-sm font-semibold mr-3 ${
                                isCurrentUser ? 'text-white' : 'text-[#4f4f4f]'
                              }`}
                            >
                              {index + 1}
                            </span>
                            <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden mr-3">
                              {provider.profile_image_url ? (
                                <img
                                  src={provider.profile_image_url}
                                  alt={provider.business_name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <span
                                  className={`text-xs font-['Open_Sans'] font-semibold ${
                                    isCurrentUser ? 'text-white' : 'text-gray-600'
                                  }`}
                                >
                                  {isCurrentUser
                                    ? 'You'
                                    : provider.business_name
                                        .split(' ')
                                        .map((n) => n[0])
                                        .join('')}
                                </span>
                              )}
                            </div>
                            <div className="flex-1">
                              <div
                                className={`font-['Open_Sans'] text-sm ${
                                  isCurrentUser ? 'font-semibold text-white' : 'text-[#1f1f1f]'
                                }`}
                              >
                                {isCurrentUser ? 'You' : provider.business_name}
                              </div>
                            </div>
                            <div className="flex items-center space-x-1">
                              <StarIcon filled={isCurrentUser} />
                              <span
                                className={`font-['Open_Sans'] text-sm ${
                                  isCurrentUser ? 'text-white' : 'text-[#1f1f1f]'
                                }`}
                              >
                                {provider.clout_score.toFixed(1)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Clout Spotlight */}
              <div className="hidden lg:block" data-testid="clout-spotlight">
                <h3 className="font-['Ovo'] text-xl text-[#1f1f1f] mb-6 uppercase">
                  CLOUT SPOTLIGHT
                </h3>
                {topProvidersLoading ? (
                  <div className="bg-[#f8f6f2] animate-pulse">
                    <div className="h-48 bg-gray-200"></div>
                    <div className="px-3 pb-3 pt-4">
                      <div className="h-5 bg-gray-200 rounded w-2/3 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                    </div>
                  </div>
                ) : spotlightProvider ? (
                  <div
                    className="bg-[#f8f6f2] relative cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => handleProviderClick(spotlightProvider.id)}
                  >
                    {/* Provider Image Section */}
                    <div className="h-48 relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-b from-[#f8f6f2] to-[#f8f6f2]">
                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2">
                          <div className="w-40 h-48 bg-gray-300 rounded-t-full overflow-hidden flex items-center justify-center">
                            {spotlightProvider.profile_image_url ? (
                              <img
                                src={spotlightProvider.profile_image_url}
                                alt={spotlightProvider.business_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-3xl font-['Open_Sans'] font-semibold text-gray-600">
                                {getNameInitials(spotlightProvider.business_name)}
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Gold star */}
                        {spotlightProvider.is_certified > 0 && (
                          <div className="absolute top-4 right-4 w-16 h-16">
                            <div className="relative">
                              <svg
                                width="64"
                                height="64"
                                viewBox="0 0 64 64"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M32 2L40.18 20.62L60 23.24L46 36.56L49.36 56L32 46.34L14.64 56L18 36.56L4 23.24L23.82 20.62L32 2Z"
                                  fill="#c6a664"
                                />
                              </svg>
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-[10px] font-['Open_Sans'] font-semibold text-white uppercase leading-tight text-center">
                                  CLOUT
                                  <br />
                                  CERTIFIED
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Text Section */}
                    <div className="px-3 pb-3 pt-4">
                      <h4 className="font-['Ovo'] text-lg text-[#1f1f1f] mb-1 uppercase">
                        {spotlightProvider.business_name}
                      </h4>
                      <p className="font-['Open_Sans'] text-xs text-[#767472]">
                        {spotlightProvider.specialties?.[0] || <NotAvailable />}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-[#f8f6f2] px-3 py-8 text-center">
                    <NotAvailable className="text-sm" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Endorse Modal */}
      <EndorseModal
        isOpen={endorseModal.isOpen}
        onClose={() => setEndorseModal({ isOpen: false, providerName: '', providerId: null })}
        providerName={endorseModal.providerName}
        providerId={endorseModal.providerId}
      />
    </div>
  );
};

export default Dashboard;
